import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Btn, Icon, SectionHead } from "../../shared/ui";
import { GOALS, reachGoal } from "../../shared/lib/metrika";
import { formatPremiumPrice, usePublicSettings } from "../../shared/lib/usePublicSettings";
import { stagger } from "../../shared/motion";
import "./faq.css";

type FaqItem = { q: string; a: string };

// «Savollar» (prototype 309–330). Single-open accordion, first item open. Every answer stays in
// the DOM (SEO); opening is CSS (grid-template-rows). The open state is the `data-open` attribute —
// not a class — so React never overwrites the `.in` reveal class the observer adds to `.qa`.
const Faq = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState<number | null>(0);
  const settings = usePublicSettings();

  // Single i18n source — also consumed by the FAQPage JSON-LD (subtask 05).
  const items = t("faq.items", {
    returnObjects: true,
    months: settings.freeTierMonths,
    price: formatPremiumPrice(settings.premiumPrice),
  }) as FaqItem[];

  return (
    <section className="sec" id="faq">
      <div className="wrap faq-grid">
        <div className="faq-side">
          <SectionHead
            eyebrow={t("faq.eyebrow")}
            title={t("faq.title")}
            sub={t("faq.subtitle")}
          />
          <div className="help" data-r>
            <span className="qt">
              <Icon name="g-tg" className="glyph" />
            </span>
            <p>{t("faq.help_text")}</p>
            <Btn
              as="a"
              variant="tint"
              size="sm"
              href={settings.supportTelegramLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => reachGoal(GOALS.telegram)}
            >
              {t("faq.help_btn")}
            </Btn>
          </div>
        </div>

        <div className="qas">
          {items.map((item, i) => {
            const isOpen = open === i;
            return (
              <div
                key={i}
                className="qa"
                data-r
                data-open={isOpen ? "" : undefined}
                style={stagger(i)}
              >
                <h3>
                  <button
                    id={`faq-q-${i}`}
                    type="button"
                    aria-expanded={isOpen}
                    aria-controls={`faq-a-${i}`}
                    onClick={() => setOpen(isOpen ? null : i)}
                  >
                    {item.q}
                    <span className="chev">
                      <Icon name="i-chevDown" />
                    </span>
                  </button>
                </h3>
                <div className="ans" id={`faq-a-${i}`} role="region" aria-labelledby={`faq-q-${i}`}>
                  <div className="ans-in">
                    <p>{item.a}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Faq;
